import { useState, useEffect } from 'react';
import { XMarkIcon, CheckIcon } from '@heroicons/react/24/outline';
import { supabase } from '../supabaseClient';

interface Reviewer {
  id?: string;
  email: string;
  name: string;
  lastSignIn?: string;
}

interface ReviewerPickerProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (selectedReviewers: Reviewer[]) => void;
}

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;

export function ReviewerPicker({ isOpen, onClose, onSubmit }: ReviewerPickerProps) {
  const [availableReviewers, setAvailableReviewers] = useState<Reviewer[]>([]);
  const [selectedReviewers, setSelectedReviewers] = useState<Reviewer[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch reviewers when modal opens
  useEffect(() => {
    if (isOpen && availableReviewers.length === 0) {
      fetchReviewers();
    }
  }, [isOpen]);

  const fetchReviewers = async () => {
    setLoading(true);
    setError(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await fetch(`${SUPABASE_URL}/functions/v1/list-reviewers`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch reviewers');
      }

      const { reviewers } = await response.json();
      setAvailableReviewers(reviewers);
      setSelectedReviewers(reviewers); // Select all by default
    } catch (err) {
      console.error('Error fetching reviewers:', err);
      setError(err instanceof Error ? err.message : 'Failed to load reviewers');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const handleToggleReviewer = (reviewer: Reviewer) => {
    setSelectedReviewers(prev => {
      const isSelected = prev.some(r => r.email === reviewer.email);
      if (isSelected) {
        return prev.filter(r => r.email !== reviewer.email);
      } else {
        return [...prev, reviewer];
      }
    });
  };

  const handleSubmit = () => {
    if (selectedReviewers.length === 0) {
      alert('Please select at least one reviewer');
      return;
    }
    onSubmit(selectedReviewers);
    onClose();
  };

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-40"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Select Reviewers</h3>
            <button
              onClick={onClose}
              className="p-1 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          {/* Body */}
          <div className="px-6 py-4">
            <p className="text-sm text-gray-600 mb-4">
              Select who should review this contract. They will receive an email with a link to review and approve.
            </p>

            {loading && (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                <p className="ml-3 text-sm text-gray-600">Loading reviewers...</p>
              </div>
            )}

            {error && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg mb-4">
                <p className="text-sm text-red-800">{error}</p>
                <button
                  onClick={fetchReviewers}
                  className="mt-2 text-sm text-red-600 hover:text-red-700 font-medium"
                >
                  Try again
                </button>
              </div>
            )}

            {!loading && !error && availableReviewers.length === 0 && (
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">No reviewers found. Users must log in at least once to appear in this list.</p>
              </div>
            )}

            {!loading && !error && availableReviewers.length > 0 && (
              <>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {availableReviewers.map(reviewer => {
                    const isSelected = selectedReviewers.some(r => r.email === reviewer.email);
                    return (
                      <label
                        key={reviewer.email}
                        className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-colors ${
                          isSelected
                            ? 'bg-primary-50 border-primary-500'
                            : 'bg-white border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleToggleReviewer(reviewer)}
                          className="w-5 h-5 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                        />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900">{reviewer.name}</p>
                          <p className="text-xs text-gray-500">{reviewer.email}</p>
                        </div>
                        {isSelected && (
                          <CheckIcon className="w-5 h-5 text-primary-600" />
                        )}
                      </label>
                    );
                  })}
                </div>

                <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>{selectedReviewers.length}</strong> reviewer{selectedReviewers.length !== 1 ? 's' : ''} selected
                  </p>
                </div>
              </>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-lg">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading || selectedReviewers.length === 0}
              className="px-4 py-2 bg-blue-600 text-white rounded-md font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Submit for Review
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

